import { Layers, Sparkles, Users, TrendingUp, DollarSign, User } from 'lucide-react';

const ServiceCard = ({ num, icon: Icon, title, subtitle, description, features, note, featured }) => (
  <div
    className={`service-card bg-white border border-gray-200 rounded-xl p-8 flex flex-col h-full group relative overflow-hidden ${
      featured ? 'border-glow' : ''
    }`}
  >
    <div className="absolute inset-0 bg-gradient-to-b from-primary/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
    <div className="relative z-10">
      <div className="flex items-center justify-between mb-6">
        <span className="font-oswald text-5xl font-bold text-gray-100">
          0{num}
        </span>
        <div className="w-12 h-12 bg-primary/10 rounded-lg flex items-center justify-center group-hover:bg-primary/20 transition-colors">
          <Icon className="w-6 h-6 text-primary" />
        </div>
      </div>
      <h3 className="font-oswald uppercase text-xl md:text-2xl font-bold text-gray-900">
        {title}
      </h3>
      <p className="font-manrope text-sm text-primary mt-2">
        {subtitle}
      </p>
      <p className="font-manrope text-sm text-gray-500 mt-4">
        {description}
      </p>
      <ul className="mt-4 space-y-2">
        {features}
      </ul>
      {note && (
        <p className="font-manrope text-xs text-gray-400 mt-4 pt-4 border-t border-gray-100 italic">
          {note}
        </p>
      )}
    </div>
  </div>
);

const Feature = ({ text }) => (
  <li className="font-manrope text-sm text-gray-700 flex items-start gap-2">
    <span className="text-primary mt-1">•</span>
    {text}
  </li>
);

export const HowWeWork = () => {
  return (
    <section
      data-testid="how-we-work-section"
      className="relative py-24 md:py-32 bg-gray-50"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        {/* Header */}
        <div className="max-w-3xl mb-16">
          <span className="font-manrope uppercase tracking-widest text-xs font-semibold text-primary">
            Our Process
          </span>
          <h2
            className="font-oswald uppercase tracking-tight font-bold text-3xl sm:text-4xl md:text-5xl lg:text-6xl leading-tight mt-4"
            data-testid="how-we-work-headline"
          >
            <span className="text-gray-900">How we turn platforms into</span>
            <br />
            <span className="text-primary">a growth engine</span>
          </h2>
          <p className="font-manrope text-base md:text-lg text-gray-500 mt-6">
            Social media isn't where the work starts. It's where everything connects.
          </p>
        </div>

        {/* Bento Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          <div data-testid="service-card-1">
            <ServiceCard
              num={1}
              icon={Layers}
              title="Platform Growth Architecture"
              subtitle="The foundation every brand needs"
              description="We design the structure behind growth:"
              featured={true}
              note="This becomes the backbone for everything that follows."
              features={
                <>
                  <Feature text="Content pillars and formats" />
                  <Feature text="Signal mapping (what the algorithm rewards)" />
                  <Feature text="Posting cadence and distribution logic" />
                </>
              }
            />
          </div>

          <div data-testid="service-card-2">
            <ServiceCard
              num={2}
              icon={Sparkles}
              title="Signal-Driven Content Engine"
              subtitle="Content designed for discovery, not polish"
              description="We build volume with intention:"
              note="Velocity, consistency, and relevance — at scale."
              features={
                <>
                  <Feature text="Platform-native creative (not traditional ads)" />
                  <Feature text="Creator-led UGC and in-house formats" />
                  <Feature text="Content engineered to generate signals consistently" />
                </>
              }
            />
          </div>

          <div data-testid="service-card-3">
            <ServiceCard
              num={3}
              icon={Users}
              title="Creator Partnership Systems"
              subtitle="From one-off posts to long-term leverage"
              description="Instead of transactional influencer campaigns, we build:"
              note="Creators become part of the growth system — not just media inventory."
              features={
                <>
                  <Feature text="Long-term creator relationships" />
                  <Feature text="Performance-led partnerships" />
                  <Feature text="Whitelisted content that compounds across channels" />
                </>
              }
            />
          </div>

          <div data-testid="service-card-4">
            <ServiceCard
              num={4}
              icon={TrendingUp}
              title="Paid Signal Amplification"
              subtitle="Paid used to scale what already works"
              description="We don't force performance through ads. We amplify proven signals:"
              features={
                <>
                  <Feature text="Organic learnings fed into paid" />
                  <Feature text="Creative testing loops" />
                  <Feature text="Sustainable performance, not artificial spikes" />
                </>
              }
            />
          </div>

          <div data-testid="service-card-5">
            <ServiceCard
              num={5}
              icon={DollarSign}
              title="Creator & Brand Monetization Infrastructure"
              subtitle="Beyond one-off brand deals"
              description="We help unlock real business value:"
              note="Monetization becomes intentional, not reactive."
              features={
                <>
                  <Feature text="Affiliate and commerce pathways" />
                  <Feature text="Licensing and reuse strategies" />
                  <Feature text="LIVE pilots where relevant" />
                </>
              }
            />
          </div>

          <div data-testid="service-card-6">
            <ServiceCard
              num={6}
              icon={User}
              title="Founder & Executive Visibility Systems"
              subtitle="Authority over attention"
              description="For leaders who want trust, not noise:"
              features={
                <>
                  <Feature text="Founder-led content systems" />
                  <Feature text="Thought leadership positioning" />
                  <Feature text="Credibility-driven visibility for B2B and enterprise" />
                </>
              }
            />
          </div>
        </div>
      </div>
    </section>
  );
};
